import { useParams } from "react-router";
import { useQuery, gql } from "@apollo/client";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import Matrix from "../components/Matrix";
import { Container, Section, Title, Text } from "tailwind-react-ui";
import Banner from "../components/Banner";
import YellowCarousel from "../components/Matrix/types/Independent/YellowCarousel";
import Journey from "../components/Matrix/types/Independent/Journey";
import QuickTips from "../components/Matrix/types/Independent/QuickTips";
import Accordion from "../components/Matrix/types/BodyContent/Accordion";
import Heading from "../components/Matrix/types/BodyContent/Heading";
import SignUpBanner from "../components/Matrix/types/Independent/SignUpBanner";
import { UPLOAD_ASSET_FRAGMENT, SITE_INFO_FRAGMENT, BODY_CONTENT_FRAGMENT, QUICK_TIPS_FRAGMENT, SIGN_UP_BANNER_FRAGMENT } from '../fragments';
import { buildFaqSchema, buildBreadcrumbSchema } from '../utils/schema';
import TriangleGY from "../images/patterns/patterns-triangles_greenyellow.svg?react";
import CableCar from "../images/patterns/pattern-arrow_cablecar.svg?react";

const truncateString = (string = "", maxLength = 54) =>
  string.length > maxLength ? `${string.substring(0, maxLength)}…` : string;

export function PlanYourVisitPage() {
  
    let { slug } = useParams();

    const GET_PLAN_DATA = gql`
        query getPlanData {
            planYourVisitEntries {
                ... on singlePlanYourVisit_Entry {
                    journey {
                        ... on step_Entry {
                            id
                            heading
                            image {
                                ...UploadAssetFragment
                            }
                            icon {
                                url
                            }
                            iconBackgroundColour
                            text
                        }
                    }
                    bodyContent {
                        ...BodyContentFragment
                    }
                    additionalText
                    yellowContentSlider {
                        ... on slide_Entry {
                            heading
                            image {
                                ...UploadAssetFragment
                            }
                            text
                            buttonText
                            buttonUrl
                            relatedEntry {
                                uri
                            }
                        }
                    }
                    quickTips {
                        ...QuickTipsFragment
                    }
                    headingMatrix {
                        ... on matrixHeading_Entry {
                            id
                            text8
                        }
                    }
                    signUpBanner {
                        ...SignUpBannerFragment
                    }
                    accordion {
                        ... on accordionRow_Entry {
                            id
                            heading
                            text
                        }
                    }
                }
            }
            planYourVisitPagesEntries(slug: "${slug}") {               
                ... on planYourVisit_Entry {
                    id
                    uri
                    url
                    title
                    typeHandle
                    subtitle
                    postDate
                    featureImage {
                        ...UploadAssetFragment
                    }
                    metaTitle
                    metaDescription
                    metaKeywords
                    metaImage {
                        ...UploadAssetFragment
                    }
                    bodyContent {
                        ...BodyContentFragment
                    }
                }
            }
            siteInfo: globalSet(handle: "siteInformation") {
                ...SiteInfoFragment 
            }
        }

        ${SITE_INFO_FRAGMENT}
        ${BODY_CONTENT_FRAGMENT}
        ${UPLOAD_ASSET_FRAGMENT}
        ${QUICK_TIPS_FRAGMENT}
        ${SIGN_UP_BANNER_FRAGMENT}
    `;

    const {loading, error, data} = useQuery(GET_PLAN_DATA);

    if (loading) return <p></p>;
    if (error) return <p>Error : {error.message}</p>;

    const landingPage = data.planYourVisitEntries[0];
    const entry = data.planYourVisitPagesEntries[0];
    const { siteInfo } = data;

  const subtitle = entry.subtitle || siteInfo.siteTitle;
  const [siteImage] = siteInfo.siteImage;
  const [featureImage = siteImage] = entry.featureImage;
  const metaImage = featureImage || entry.metaImage || siteInfo.siteImage;
  const metaDescription = truncateString((entry.metaDescription || entry.summary || siteInfo.siteDescription).replace(/(<([^>]+)>)/gi, ""), 159);
  const metaTitle = truncateString(`${entry.metaTitle || entry.displayTitle || entry.title}`, 53);
  const metaKeywords = entry.metaKeywords || siteInfo.siteKeywords || null;
  const metaImageFormatted = `${metaImage.url}`;
  const faqSchema = entry.uri === "plan-your-visit/fast-track" ? buildFaqSchema(landingPage.accordion) : null;
  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: "Home", path: "/" },
    { name: entry.title, path: `/${entry.uri}` },
  ]);

  return (
    <Layout location={entry.url} title={metaTitle}>
      <Seo
        title={metaTitle}
        description={metaDescription}
        keywords={metaKeywords}
        image={metaImageFormatted}
        url={entry.url}
        pathname={`/${entry.uri}`}
        structuredData={[faqSchema, breadcrumbSchema].filter(Boolean)}
      />
      <h1 className="sr-only">{metaTitle}</h1>
        {featureImage && (
            <Banner
                block={{
                    variation: "secondary",
                    slides: [{
                        title: subtitle,
                        subtitle: entry.title,
                        image: [featureImage],
                    }]}
                }
            />
        )}

      {entry.uri === "plan-your-visit/fast-track" && (
        <>
          {landingPage.additionalText && (
            <Section id="fastTrack" bg="white" className="relative" p="0">
              <Container className="py-14 lg:pt-20 w-full xl:w-3/5">
                <Title
                  level={3}
                  text="primary"
                  font="heading"
                  className="text-primary font-heading lg:text-5xl relative flex justify-center flex-start"
                >
                  Fast Track
                  <TriangleGY className="ml-6 -mt-1" alt="Triangles" />
                </Title>
                <Matrix blocks={entry.bodyContent} />
                <div
                  className="py-4 secondary-page"
                  dangerouslySetInnerHTML={{
                    __html: landingPage.additionalText,
                  }}
                />
              </Container>
            </Section>
          )}

          {landingPage.accordion.length > 0 && (
            <Section
              id="faqs"
              bg="blue-dark"
              className="bg-blue-dark relative mb-8"
              p="0"
            >
              <Container className="xl:w-3/4 pt-20 pb-24">
                <Heading
                  block={{
                    hierarchy: 2,
                    align: "center",
                    text: landingPage.headingMatrix[0].text,
                    colour: "white",
                  }}
                  margins="mb-14"
                />
                {landingPage.accordion.map((block, i) => (
                  <Accordion
                    key={`acc${i}`}
                    arrow="#e5e7eb"
                    theme="white"
                    block={block}
                  />
                ))}
              </Container>
            </Section>
          )}

          <Section>
            <Container>
              <SignUpBanner block={landingPage.signUpBanner[0]} />
            </Container>
          </Section>
        </>
      )}

      {entry.uri === "plan-your-visit/specials" && (
        <Section id="specials" p="0">
          <Container className="pt-14 pb-8 w-full xl:w-3/4">
            <Title
              level={3}
              text="primary"
              font="heading"
              className="font-heading text-primary lg:text-5xl relative flex justify-center flex-start"
            >
              Our Specials
              <TriangleGY className="ml-6 -mt-1" alt="Triangles" />
            </Title>
            {landingPage.yellowContentSlider ? (
              <YellowCarousel items={landingPage.yellowContentSlider} />
            ) : null}
          </Container>
        </Section>
      )}

      {entry.uri !== "plan-your-visit/fast-track" && entry.bodyContent.length && (
            <Matrix blocks={entry.bodyContent} />
      )}

      {entry.uri === "plan-your-visit/getting-here" && (
        <>
          {landingPage.bodyContent.length && (
                <Matrix blocks={landingPage.bodyContent} />
          )}

          <Section id="tips" p="0" className="p-0">
            <Container className="pt-14 pb-8 lg:pt-20 lg:pb-14 w-full xl:w-3/4">
              <Title
                level={4}
                text="primary"
                font="heading"
                className="text-primary font-heading lg:text-5xl relative flex justify-center flex-start mb-5"
              >
                Quick Tips
                <TriangleGY className="ml-6 -mt-1" alt="Triangles" />
              </Title>
              <Text is="p" className="text-center text-md">
                Important info to know before you go. Got a question for us?
                Give us a call on (021) 424 8181, tweet us @tablemountainca,
                post on our Facebook wall or email us on info@tablemountain.net.
              </Text>

              {landingPage.quickTips ? (
                <>
                  <hr className="border-0 block my-14" />
                  <div className="shadow-md p-6 sm:p-10 bg-white rounded-lg">
                    <QuickTips block={landingPage.quickTips} />
                  </div>
                </>
              ) : null}
            </Container>
          </Section>

          <Section id="journey" p="0" className="p-0">
            <Container className="pt-14 pb-0 lg:py-20">
              <Title
                level={2}
                font="heading"
                className="font-heading text-center md:text-3xl lg:text-5xl relative flex justify-center flex-start"
              >
                {landingPage.displayTitle}
                <CableCar className="ml-1 -mr-5 -mt-1" alt="Cable Car icon" />
              </Title>
              <Journey block={landingPage.journey} />
            </Container>
          </Section>
        </>
      )}
    </Layout>
  );
};
